import React from 'react';
import { motion } from 'framer-motion';

export const SplashScreen = () => {
  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-[#050505]">
      {/* Background glow */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{ duration: 3, repeat: Infinity }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-violet-600/30 blur-[100px]"
        />
        <motion.div
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{ duration: 4, repeat: Infinity, delay: 0.5 }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full bg-fuchsia-600/20 blur-[80px]"
        />
      </div>

      {/* Logo */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 flex flex-col items-center"
      >
        {/* Icon */}
        <motion.div
          animate={{ rotateY: [0, 360] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          className="w-20 h-20 mb-6 rounded-2xl bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center shadow-neon-lg"
        >
          <span className="font-display text-4xl text-white">N</span>
        </motion.div>

        {/* Text */}
        <motion.h1
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="font-heading text-3xl text-white uppercase tracking-wider mb-2"
        >
          The Industry
        </motion.h1>
        <motion.p
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="text-violet-400 text-sm uppercase tracking-widest"
        >
          Nashville
        </motion.p>
      </motion.div>

      {/* Loading indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="absolute bottom-20"
      >
        <div className="flex gap-1.5">
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.3, 1, 0.3],
              }}
              transition={{
                duration: 0.8,
                repeat: Infinity,
                delay: i * 0.15,
              }}
              className="w-2 h-2 rounded-full bg-violet-500"
            />
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export const PageLoader = () => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="relative">
        {/* Glow */}
        <div className="absolute inset-0 rounded-full bg-violet-600/20 blur-xl animate-pulse" />

        {/* Spinner */}
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="relative w-12 h-12 rounded-full border-2 border-zinc-800"
        >
          <div className="absolute top-0 left-0 w-full h-full rounded-full border-2 border-transparent border-t-violet-500" />
        </motion.div>
      </div>
    </div>
  );
};

export const SkeletonCard = ({ className = '' }) => {
  return (
    <div
      className={`rounded-2xl bg-zinc-900/50 border border-zinc-800 overflow-hidden ${className}`}
    >
      <div className="p-6 space-y-4">
        <div className="h-6 w-2/3 rounded-lg bg-zinc-800 animate-pulse" />
        <div className="h-4 w-1/2 rounded-lg bg-zinc-800/60 animate-pulse" />
        <div className="space-y-2">
          <div className="h-3 w-full rounded bg-zinc-800/40 animate-pulse" />
          <div className="h-3 w-4/5 rounded bg-zinc-800/40 animate-pulse" />
        </div>
      </div>
    </div>
  );
};

export const SkeletonAvatar = ({ size = 'md' }) => {
  const sizes = {
    sm: 'w-10 h-10',
    md: 'w-16 h-16',
    lg: 'w-24 h-24',
  };

  return <div className={`${sizes[size]} rounded-full bg-zinc-800 animate-pulse`} />;
};

export const SkeletonText = ({ width = 'full', height = '4' }) => {
  return <div className={`h-${height} w-${width} rounded bg-zinc-800 animate-pulse`} />;
};

export default SplashScreen;
